/**
 * DeploymentCommandQueue — Admin → Viral
 * ------------------------------------------------------------------
 * Tactical command queue for individual asset-level deployment.
 * Operators can stage, mark live, retire, escalate priority,
 * delay, or pivot priority across the deployment pipeline.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crosshair, ArrowUp, ArrowDown, Pause, Rocket, Skull, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  DEPLOYMENT_EVENT,
  STATUS_TONE,
  adjustAssetPriority,
  fetchDeploymentAssets,
  setAssetStatus,
  type DeploymentAssetRow,
} from "@/lib/deploymentEngine";

const SAT_TONE: Record<string, string> = {
  fresh: "text-cyan",
  rising: "text-emerald-300",
  peaking: "text-violet-300",
  saturating: "text-amber-300",
  burnt: "text-red-400",
};
const FAT_TONE: Record<string, string> = {
  low: "text-cyan",
  moderate: "text-emerald-300",
  elevated: "text-amber-300",
  critical: "text-red-400",
};

export default function DeploymentCommandQueue() {
  const [items, setItems] = useState<DeploymentAssetRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setItems(await fetchDeploymentAssets());
    setLoading(false);
  };

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener(DEPLOYMENT_EVENT, refresh);
    const ch = (supabase as any)
      .channel("deploy-assets")
      .on("postgres_changes", { event: "*", schema: "public", table: "deployment_assets" }, refresh)
      .subscribe();
    return () => {
      window.removeEventListener(DEPLOYMENT_EVENT, refresh);
      (supabase as any).removeChannel(ch);
    };
  }, []);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Crosshair className="w-5 h-5 text-cyan animate-pulse" />
          Deployment Command Queue
          <Badge variant="outline" className="ml-2 text-[10px] border-cyan/40 text-cyan">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && <div className="text-xs text-muted-foreground">Pulling deployment manifest...</div>}
        {!loading && items.length === 0 && (
          <div className="text-xs text-muted-foreground italic">
            No assets in deployment pipeline. Stage campaigns from <span className="text-cyan">Strategy → Live Behavioral Operations</span>.
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="rounded border border-glass-border bg-charcoal-deep/60 p-2.5 flex items-center gap-3">
            <div className="flex flex-col items-center gap-0.5 w-8">
              <div className="text-[9px] uppercase text-muted-foreground">P</div>
              <div className="text-cyan font-mono text-sm">{a.deployment_priority}</div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] uppercase tracking-wider ${STATUS_TONE[a.deployment_status]}`}>
                  {a.deployment_status}
                </Badge>
                <span className="text-[10px] text-muted-foreground font-mono">
                  {a.platform ?? "multi"} · {a.id.slice(0, 8)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[10px] mt-1 font-mono">
                <span className="text-muted-foreground">proj <span className="text-cyan">{Math.round(a.projected_performance)}%</span></span>
                <span className="text-muted-foreground">act <span className="text-emerald-300">{Math.round(a.actual_performance)}%</span></span>
                <span className={SAT_TONE[a.saturation_state] ?? "text-muted-foreground"}>{a.saturation_state}</span>
                <span className={FAT_TONE[a.fatigue_state] ?? "text-muted-foreground"}>fatigue: {a.fatigue_state}</span>
                {a.decay_score > 30 && <span className="text-orange-300">decay {Math.round(a.decay_score)}</span>}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" title="Escalate priority"
                className="h-7 w-7 border-cyan/30 text-cyan hover:bg-cyan/10"
                onClick={() => adjustAssetPriority(a.id, +1)}>
                <ArrowUp className="w-3 h-3" />
              </Button>
              <Button size="icon" variant="outline" title="Lower priority"
                className="h-7 w-7 border-muted/40 text-muted-foreground hover:bg-muted/20"
                onClick={() => adjustAssetPriority(a.id, -1)}>
                <ArrowDown className="w-3 h-3" />
              </Button>
              {a.deployment_status === "staged" && (
                <Button size="sm" variant="outline" className="h-7 text-[10px] border-cyan/40 text-cyan"
                  onClick={() => setAssetStatus(a.id, "deployed")}>
                  <Rocket className="w-3 h-3 mr-1" /> Deploy
                </Button>
              )}
              {a.deployment_status === "deployed" && (
                <Button size="sm" variant="outline" className="h-7 text-[10px] border-emerald-400/40 text-emerald-300"
                  onClick={() => setAssetStatus(a.id, "live")}>
                  <Zap className="w-3 h-3 mr-1" /> Live
                </Button>
              )}
              {!["scheduled", "retired"].includes(a.deployment_status) && (
                <Button size="icon" variant="outline" title="Delay deployment"
                  className="h-7 w-7 border-violet-400/40 text-violet-300 hover:bg-violet-400/10"
                  onClick={() => setAssetStatus(a.id, "scheduled")}>
                  <Pause className="w-3 h-3" />
                </Button>
              )}
              {a.deployment_status !== "retired" && (
                <Button size="icon" variant="outline" title="Retire asset"
                  className="h-7 w-7 border-red-500/40 text-red-400 hover:bg-red-500/10"
                  onClick={() => setAssetStatus(a.id, "retired")}>
                  <Skull className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
